import React, { Fragment } from 'react';
import api from 'constants/api';
import payMethods from 'constants/payMethods';
import axios from 'helpers/axios';
import IconHeader from 'components/IconHeader';
import AxiosPageReadyWrapper from 'components/AxiosPageReadyWrapper';
import { snakeCaseToTitleCase } from '@bonlineza/b-lib/functions';
import LuxityLoader from 'components/LuxityLoader';
import GAButton from 'components/GAButton';
import { error as notifyError } from '@bonlineza/luxity-lib/Notify';
import requestErrorMessages from 'constants/requestErrorMessages';
import withRouter from 'helpers/withRouter';
import checkMinimumPaymentAmount from 'functions/checkMinimumPaymentAmount';
import scrollToElement from 'functions/scrollToElement';
import PaymentItem from './components/PaymentItem';

const initialProcessState = {
  is_processing: false,
};

class Payment extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      ...initialProcessState,
      hasScrolled: false,
      order: {},
      sales_id: props.params.order_number,
      payment_methods: [],
      usable_customer_credit: 0,
      field_validation_err_exists: false,
      fields: {
        current_payment_method: {
          value: null,
          validation_msg: null,
        },
        amount: {
          value: null,
          validation_msg: null,
        },
      },
    };

    this.paymentInputRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.params.order_number && !this.state.hasScrolled) {
      window.setTimeout(() => {
        const targetElement = document.getElementById('payment-method-section');
        scrollToElement(targetElement);
        this.setState({ hasScrolled: true });
      }, 500);
    }
  }

  reintializeProcessState = () => {
    this.setState({
      ...initialProcessState,
    });
  };

  sendPayment = () => {
    this.reintializeProcessState();
    const {
      fields: {
        amount: { value: paymentAmount },
      },
    } = this.state;
    this.setState({ is_processing: true });
    axios
      .post(api.data.PAY_OUTSTANDING_AMOUNT, {
        amount: paymentAmount,
        sale_id: this.state.sales_id,
      })
      .then((res) => {
        if (res.data.redirect_url) {
          window.location.href = res.data.redirect_url;
        }
      })
      .catch((e) => {
        const { status, data } = e;
        this.afterSubmitErrors(status, data);

        this.setState({
          is_processing: false,
        });
      });
  };

  afterSubmitErrors = (status = null, data = {}) => {
    if (status === 422) {
      const key = Object.keys(data)[0];
      const validationMsg = data.amount[0] || '';

      return this.setState((prevState) => ({
        ...prevState,
        field_validation_err_exists: true,
        fields: {
          ...prevState.fields,
          [key]: {
            ...prevState.fields.amount,
            validation_msg: validationMsg,
          },
        },
      }));
    }
    return notifyError(requestErrorMessages.RESPONSE_500);
  };

  handleCustomerCreditPayment = () => {
    this.reintializeProcessState();
    const { usable_customer_credit } = this.state;
    this.setState({ is_processing: true });
    axios
      .post(api.data.PAY_WITH_CREDIT, {
        credit_total: usable_customer_credit,
        sale_id: this.state.sales_id,
      })
      .then((response) => {
        const { sale_order_number, amount } = response.data.payload;
        window.location.href = `/credit-payment-result?sale_order_number=${
          sale_order_number || ''
        }&amount=${amount || ''}`;
      })
      .catch(() => {
        notifyError(requestErrorMessages.RESPONSE_500);
        setTimeout(() => {
          this.props.history.push('/orders');
          window.location.reload();
        }, 3000);
      })
      .finally(() => {
        this.setState({
          is_processing: false,
        });
      });
  };

  handleValidation = () => {
    this.setState({ field_validation_err_exists: false });
    const dataKeys = Object.keys(this.state.fields);
    Object.values(this.state.fields).forEach((field, key) => {
      switch (true) {
        case this.paymentInputRef.current.validity.badInput:
          this.setState((prevState) => ({
            ...prevState,
            field_validation_err_exists: true,
            fields: {
              ...prevState.fields,
              amount: {
                ...prevState.fields.amount,
                validation_msg: `This field only accepts numbers`,
              },
            },
          }));
          break;

        case !field.value:
          this.setState((prevState) => ({
            ...prevState,
            field_validation_err_exists: true,
            fields: {
              ...prevState.fields,
              [dataKeys[key]]: {
                ...prevState.fields[dataKeys[key]],
                validation_msg: `${snakeCaseToTitleCase(
                  dataKeys[key],
                )} is mandatory`,
              },
            },
          }));
          break;

        case parseInt(this.state.fields.amount.value, 10) >
          parseInt(this.state.order.outstanding_balance, 10): {
          this.setState((prevState) => ({
            ...prevState,
            field_validation_err_exists: true,
            fields: {
              ...prevState.fields,
              amount: {
                ...prevState.fields.amount,
                validation_msg:
                  'The amount you are paying is more than the outstanding balance',
              },
            },
          }));

          break;
        }
        case parseInt(this.state.fields.amount.value, 10) < 0: {
          this.setState((prevState) => ({
            ...prevState,
            field_validation_err_exists: true,
            fields: {
              ...prevState.fields,
              amount: {
                ...prevState.fields.amount,
                validation_msg: 'Amount needs to be bigger than 0',
              },
            },
          }));

          break;
        }

        case checkMinimumPaymentAmount(
          this.state.order.outstanding_balance,
          this.state.order.order_total,
          this.state.fields.amount.value,
        ): {
          this.setState((prevState) => ({
            ...prevState,
            field_validation_err_exists: true,
            fields: {
              ...prevState.fields,
              amount: {
                ...prevState.fields.amount,
                validation_msg:
                  'The payment amount must be at least 25% of the order total.',
              },
            },
          }));

          break;
        }

        default:
          break;
      }
    });
    this.setState(
      (prevState) => ({
        ...prevState,
      }),
      () => {
        if (!this.state.field_validation_err_exists) {
          this.sendPayment();
        }
      },
    );
  };

  handleInputChange = (value = null, fieldName) => {
    this.setState((prevState) => ({
      ...prevState,
      fields: {
        ...prevState.fields,
        [fieldName]: {
          ...prevState.fields[fieldName],
          value,
          validation_msg: null,
        },
      },
    }));
  };

  handleGetPayInfoError = (status = null) =>
    status === 404 ? this.props.history.push('/') : false;

  render = () => (
    <Fragment>
      {this.props.params.order_number ? (
        <AxiosPageReadyWrapper
          url={`${api.data.GET_PAYMENT_INFO}/${this.props.params.order_number}`}
          customErrorHandler={this.handleGetPayInfoError}
          onData={({
            data: { order, payment_methods, usable_customer_credit },
          }) =>
            this.setState({
              order,
              payment_methods,
              usable_customer_credit,
            })
          }>
          <Fragment>
            <IconHeader title="Make Payment" svgName="my_payment" />

            <div className="payment-page-layout">
              <div className="payment-page-layout__summary">
                <div className="orders">
                  <PaymentItem data={this.state.order} />
                </div>
              </div>
              <div className="payment-page-layout__actions">
                <div className="payment-panel__title">
                  Select Payment Method
                </div>

                {this.state.fields.current_payment_method.validation_msg && (
                  <div className="payment-error-msg">
                    {this.state.fields.current_payment_method.validation_msg}
                  </div>
                )}
                <div
                  id="payment-method-section"
                  className={`payment-method-options${
                    this.state.fields.current_payment_method.validation_msg
                      ? ' payment-method-options--highlighted'
                      : ''
                  }`}>
                  {this.state.payment_methods.map((method, key) => (
                    <div
                      key={key}
                      className={`payment-method-option${
                        this.state.fields.current_payment_method.value ===
                        method.key
                          ? ` payment-method-option--active`
                          : ''
                      }`}>
                      <label htmlFor={`payment_method-${key}`}>
                        <input
                          type="radio"
                          id={`payment_method-${key}`}
                          name="current_payment_method"
                          value={method.key}
                          checked={
                            this.state.fields.current_payment_method.value ===
                            method.key
                          }
                          onChange={(e) => {
                            e.preventDefault();
                            this.handleInputChange(
                              e.target.value,
                              'current_payment_method',
                            );
                          }}
                        />
                        <div className="payment-method-option__content">
                          <span className="payment-method-option__text">
                            {method.name}
                          </span>
                          {method.key === payMethods.EFT && (
                            <img
                              src="/assets/images/checkout/eft-icon.png"
                              alt="EFT"
                              width={14}
                              height={14}
                              className="payment-method-option__icon"
                            />
                          )}
                          {method.key === payMethods.CREDIT_CARD && (
                            <img
                              src="/assets/images/checkout/credit-card-icon.png"
                              alt="Credit Card"
                              width={14}
                              height={14}
                              className="payment-method-option__icon"
                            />
                          )}
                        </div>
                      </label>
                    </div>
                  ))}
                </div>

                {this.state.fields.current_payment_method.value ===
                  payMethods.CREDIT_CARD && (
                  <Fragment>
                    <div className="payment-panel__title">
                      Enter Payment Amount
                    </div>
                    {this.state.fields.amount.validation_msg && (
                      <div className="payment-error-msg">
                        {this.state.fields.amount.validation_msg}
                      </div>
                    )}
                    <div
                      className={`payment-field-card payment-field-card--payment-row${
                        this.state.fields.amount.validation_msg
                          ? ' payment-field-card--highlighted'
                          : ''
                      }`}>
                      <div className="payment-field-card__row payment-field-card__row--payment">
                        <div className="payment-field-card__input-wrap">
                          <input
                            className={`payment-field-input${
                              this.state.fields.amount.validation_msg
                                ? ' payment-field-input--error'
                                : ''
                            }`}
                            name="amount"
                            ref={this.paymentInputRef}
                            onChange={(e) => {
                              e.preventDefault();
                              this.handleInputChange(e.target.value, 'amount');
                            }}
                            type="number"
                            placeholder="Enter Amount R"
                          />
                        </div>
                        <div className="payment-field-card__cta payment-field-card__cta--inline">
                          {this.state.is_processing ? (
                            <LuxityLoader />
                          ) : (
                            <GAButton
                              className="payment-action-button"
                              label="Make Payment"
                              onClick={(e) => {
                                e.preventDefault();
                                this.handleValidation();
                              }}>
                              <div className="flex gap-4 items-center">
                                Make Payment
                              </div>
                            </GAButton>
                          )}
                        </div>
                      </div>
                    </div>
                  </Fragment>
                )}

                {this.state.fields.current_payment_method.value ===
                  payMethods.EFT && (
                  <Fragment>
                    <div className="payment-panel__title">Bank Details</div>
                    <div className="payment-field-card payment-field-card--info">
                      <div className="payment-detail-grid">
                        <div className="payment-detail-item">
                          <span className="payment-detail-item__label">
                            Bank
                          </span>
                          <span className="payment-detail-item__value">
                            FNB
                          </span>
                        </div>
                        <div className="payment-detail-item">
                          <span className="payment-detail-item__label">
                            Branch Code
                          </span>
                          <span className="payment-detail-item__value">
                            250 655
                          </span>
                        </div>
                        <div className="payment-detail-item">
                          <span className="payment-detail-item__label">
                            Account Type
                          </span>
                          <span className="payment-detail-item__value">
                            Cheque
                          </span>
                        </div>
                        <div className="payment-detail-item">
                          <span className="payment-detail-item__label">
                            Account Name
                          </span>
                          <span className="payment-detail-item__value">
                            Luxity (pty) ltd
                          </span>
                        </div>
                        <div className="payment-detail-item payment-detail-item--accent">
                          <span className="payment-detail-item__label">
                            Account Number
                          </span>
                          <span className="payment-detail-item__value">
                            62556319509
                          </span>
                        </div>
                        <div className="payment-detail-item payment-detail-item--accent">
                          <span className="payment-detail-item__label">
                            Reference
                          </span>
                          <span className="payment-detail-item__value">
                            {this.state.order.order_number}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Fragment>
                )}

                {this.state.fields.current_payment_method.value ===
                  payMethods.CUSTOMER_CREDIT && (
                  <div className="payment-field-card">
                    <div className="payment-field-card__row payment-field-card__row--mobile-stack">
                      <div className="payment-field-card__copy">
                        <div className="payment-field-card__label">
                          Use My Credit
                        </div>
                        <div className="payment-field-card__hint">
                          Apply R
                          {this.state.usable_customer_credit.toLocaleString()}
                          {` `}
                          now.
                        </div>
                      </div>
                      <div className="payment-field-card__cta">
                        {this.state.is_processing ? (
                          <LuxityLoader />
                        ) : (
                          <GAButton
                            className="payment-action-button"
                            label="Make Payment"
                            onClick={(e) => {
                              e.preventDefault();
                              this.handleCustomerCreditPayment();
                            }}>
                            <div className="flex gap-4 items-center">
                              Make Payment
                            </div>
                          </GAButton>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Fragment>
        </AxiosPageReadyWrapper>
      ) : (
        'No Order number supplied'
      )}
    </Fragment>
  );
}

export default withRouter(Payment);
